/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { mergeStyleSets } from '@fluentui/merge-styles';
import { Announced } from '@fluentui/react/lib/Announced';
import { CommandBarButton, CommandButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { IIconProps } from '@fluentui/react/lib/Icon';
import { IOverflowSetItemProps } from '@fluentui/react/lib/OverflowSet';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITooltipProps, ITooltipStyles } from '@fluentui/react/lib/Tooltip';
import { IRenderFunction, classNamesFunction } from '@fluentui/react/lib/Utilities';
import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/style-utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { GlobalProfileDropdown } from 'src/appShell/globalHeader/globalHeaderBlades/GlobalProfileDropdown';
import { NotificationsIcon } from 'src/appShell/globalHeader/loggedInHeader/NotificationsIcon';
import { GlobalHeaderBladeType } from 'src/appShell/globalHeader/types';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { getScopedTheme } from 'src/core/themes/luisStylist';

const headerTheme = getScopedTheme('LoggedInHeader');
const boldItemStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { fontSize: 16 } });
const headerButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: {
        height: '100%',
        overflow: 'auto',
        whiteSpace: 'nowrap',
        color: headerTheme.textColor,
        backgroundColor: headerTheme.backgroundColor
    },
    rootHovered: { backgroundColor: headerTheme.backgroundColorHovered, color: headerTheme.textColor },
    rootPressed: { backgroundColor: headerTheme.backgroundColorActive, color: headerTheme.textColor },
    icon: { padding: headerTheme.iconPadding, color: headerTheme.textColor },
    iconPressed: { backgroundColor: headerTheme.backgroundColorActive, color: headerTheme.textColor },
    iconHovered: { backgroundColor: headerTheme.backgroundColorHovered, color: headerTheme.textColor },
    menuIcon: { color: headerTheme.textColor },
    menuIconHovered: { backgroundColor: headerTheme.backgroundColorHovered, color: headerTheme.textColor },
    menuIconPressed: { backgroundColor: headerTheme.backgroundColorActive, color: headerTheme.textColor }
});
const selectedHeaderButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { height: '100%', backgroundColor: headerTheme.textColor, color: headerTheme.backgroundColorHovered, overflow: 'auto' },
    rootHovered: { height: '100%', backgroundColor: headerTheme.textColor, color: headerTheme.backgroundColorHovered, overflow: 'auto' },
    icon: { padding: headerTheme.iconPadding, backgroundColor: headerTheme.textColor, color: headerTheme.backgroundColorHovered },
    iconHovered: { padding: headerTheme.iconPadding, backgroundColor: headerTheme.textColor, color: headerTheme.backgroundColorHovered }
});
const headerButtonFocusClassName = mergeStyles(getFocusStyle(getTheme(), { highContrastStyle: { outlineOffset: '-8px' } }));

const messages = defineMessages({
    moreItemsAriaLabel: {
        id: 'portal.loggedInHeader.moreItemsAriaLabel',
        defaultMessage: 'More items'
    },
    notificationInProgressMessage: {
        id: 'portal.loggedInHeader.notificationInProgressMessage',
        defaultMessage: 'Notification center have updates'
    }
});

/**
 * Renders the button that will appear on the left
 * side of the command bar.
 *
 * @param item The item to render.
 */
export const onRenderLoggedInHeaderItem = (item: IOverflowSetItemProps) => {
    const commandButton = (
        <CommandButton
            key={item.key}
            role="menuitem"
            rel={item.rel as string}
            text={item.text as string}
            href={item.href as string}
            data-automation-id={item.key}
            target={item.target as string}
            disabled={item.disabled as boolean}
            className={headerButtonFocusClassName}
            iconProps={item.iconProps as IIconProps}
            menuProps={item.subMenuProps as IContextualMenuProps}
            tabIndex={item.key === 'languageStudioSeparator' ? -1 : 0}
            styles={mergeStyleSets(headerButtonStyles, boldItemStyles)}
            onClick={item.onClick as React.MouseEventHandler<HTMLButtonElement>}
        />
    );
    const tooltipStyles = classNamesFunction<ITooltipProps, ITooltipStyles>()({ root: { height: '100%' } });

    if (typeof item.content === 'string') {
        return (
            <LuisTooltip key={item.key} isDarkMode={false} content={item.content} styles={tooltipStyles}>
                {commandButton}
            </LuisTooltip>
        );
    }
    return commandButton;
};

/**
 * Renders the items that will display on the right
 * of the command bar.
 *
 * @param item the item to render.
 */
export const onRenderLoggedInHeaderFarItem = (
    item: IOverflowSetItemProps,
    activeBlade: GlobalHeaderBladeType,
    localize: ReactIntl.InjectedIntl['formatMessage']
) => {
    if (item.notification) {
        return (
            <LuisTooltip isDarkMode={false} content={item.content as string}>
                <Announced role="menuitem" message={`${item.isInProgress ? '' : localize(messages.notificationInProgressMessage)}`} />

                <NotificationsIcon
                    id={item.key}
                    role="menuitem"
                    text={item.text as string}
                    count={item.count as number}
                    className={headerButtonFocusClassName}
                    ariaLabel={item.ariaLabel as string}
                    isInProgress={item.isInProgress as boolean}
                    onClick={item.onClick as React.MouseEventHandler<HTMLButtonElement>}
                    styles={item.key === activeBlade ? selectedHeaderButtonStyles : headerButtonStyles}
                />
            </LuisTooltip>
        );
    }
    return (
        <LuisTooltip isDarkMode={false} content={item.content as string}>
            <Stack horizontal verticalFill>
                <CommandBarButton
                    id={item.key}
                    key={item.key}
                    role="menuitem"
                    text={item.text as string}
                    className={headerButtonFocusClassName}
                    ariaLabel={item.ariaLabel as string}
                    iconProps={item.iconProps as IIconProps}
                    onClick={item.onClick as React.MouseEventHandler<HTMLButtonElement>}
                    onRenderChildren={item.onRenderChildren as IRenderFunction<IButtonProps>}
                    styles={item.key === activeBlade ? selectedHeaderButtonStyles : headerButtonStyles}
                />
                {item.key === 'avatar' && (
                    <GlobalProfileDropdown
                        isFocused={item.isProfileMenuOpen as boolean}
                        isVisible={item.isProfileMenuOpen as boolean}
                        onDismiss={item.onProfileCalloutDismiss as () => void}
                    />
                )}
            </Stack>
        </LuisTooltip>
    );
};

/**
 * Renders the more button that display that won't
 * appear in the command bar.
 *
 * @param overflowItems The items that don't currently
 * fit the command bar and should be truncated.
 */
export const onRenderLoggedInHeaderMoreButton = (
    overflowItems: IOverflowSetItemProps[],
    localize: ReactIntl.InjectedIntl['formatMessage']
) => (
    <CommandBarButton
        role="menuitem"
        styles={headerButtonStyles}
        menuIconProps={{ iconName: 'More' }}
        menuProps={{ items: overflowItems }}
        title={localize(messages.moreItemsAriaLabel)}
    />
);
